import { FileText, Download, Eye, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BillingDocument } from '@/hooks/useBillingDocuments';
import { format } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface BillingDocumentCardProps {
  document: BillingDocument;
  onView: (document: BillingDocument) => void;
  onDownload: (document: BillingDocument) => void;
}

export const BillingDocumentCard = ({ document, onView, onDownload }: BillingDocumentCardProps) => {
  const { t, i18n } = useTranslation();

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'de': return de;
      case 'it': return it;
      default: return enUS;
    }
  };

  const getStatusBadge = () => {
    switch (document.status) {
      case 'paid':
        return (
          <Badge className="bg-[#14A38B] text-white">
            <CheckCircle size={14} className="mr-1" />
            {t('billing.status.paid')}
          </Badge>
        );
      case 'sent':
        return (
          <Badge className="bg-[#0880AE] text-white">
            <Clock size={14} className="mr-1" />
            {t('billing.status.sent')}
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className="bg-[#FF7171] text-white">
            <XCircle size={14} className="mr-1" />
            {t('billing.status.cancelled')}
          </Badge>
        );
      default:
        return (
          <Badge className="bg-[#87ADD4] text-white">
            {t('billing.status.draft')}
          </Badge>
        );
    }
  };

  const getDocumentTypeLabel = () => {
    return document.document_type === 'order_form' 
      ? t('billing.documentTypes.orderForm') 
      : t('billing.documentTypes.invoice');
  };

  const getDocumentIcon = () => {
    return document.document_type === 'order_form' ? (
      <FileText className="text-[#0880AE]" size={20} />
    ) : (
      <FileText className="text-[#FFBF07]" size={20} />
    );
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-300 border-[#DBE2EA]">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {getDocumentIcon()}
            <div>
              <CardTitle className="text-lg font-semibold text-[#37699E]">
                {document.document_number}
              </CardTitle>
              <p className="text-sm text-[#5A6B7A]">{getDocumentTypeLabel()}</p>
            </div>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Order Info */}
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <span className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('billing.card.order')}
            </span>
            <span className="text-sm text-[#2D3748]">
              {document.orders?.order_number}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('billing.card.route')}
            </span>
            <span className="text-sm text-[#2D3748]">
              {document.orders?.pickup_city} → {document.orders?.delivery_city}
            </span>
          </div>
        </div>

        {/* Amount */}
        <div className="bg-[#EBF4F8] rounded-lg p-3">
          <div className="flex justify-between items-baseline">
            <span className="text-base font-semibold text-[#37699E]">{t('billing.card.amountHT')}</span>
            <span className="text-xl font-bold text-[#37699E]">
              {document.amount.toFixed(2)} €
            </span>
          </div>
        </div>

        {/* Dates */}
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-[#5A6B7A]">{t('billing.card.issuedOn')}</span>
            <span className="text-[#2D3748]">
              {format(new Date(document.issued_at), 'dd MMM yyyy', { locale: getDateLocale() })}
            </span>
          </div>
          {document.due_date && (
            <div className="flex justify-between text-sm">
              <span className="text-[#5A6B7A]">{t('billing.card.dueDate')}</span>
              <span className="text-[#2D3748]">
                {format(new Date(document.due_date), 'dd MMM yyyy', { locale: getDateLocale() })}
              </span>
            </div>
          )}
          {document.paid_at && (
            <div className="flex justify-between text-sm">
              <span className="text-[#5A6B7A]">{t('billing.card.paidOn')}</span>
              <span className="text-[#14A38B] font-medium">
                {format(new Date(document.paid_at), 'dd MMM yyyy', { locale: getDateLocale() })}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 border-[#0880AE] text-[#0880AE] hover:bg-[#EBF4F8]"
            onClick={() => onView(document)}
          >
            <Eye size={16} className="mr-2" />
            {t('billing.card.view')}
          </Button>
          <Button
            size="sm"
            className="flex-1 bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748]"
            onClick={() => onDownload(document)}
          >
            <Download size={16} className="mr-2" />
            {t('billing.card.pdf')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
